import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Shield, CheckCircle, XCircle } from 'lucide-react'
const API = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
async function publicRpc(fn: string, args: Record<string, any> = {}) {
  const res = await fetch(`${API}/api/public/rpc/${fn}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(args),
  })
  return res.json()
}
import { FirewallChangeRequestRow } from '@/types'
import { useTranslation } from 'react-i18next'

export const PublicFirewallChangeRequestPage: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  
  const [loading, setLoading] = useState(true)
  const [validating, setValidating] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [tokenValid, setTokenValid] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [hasValidated, setHasValidated] = useState(false)
  
  const [formData, setFormData] = useState<Partial<FirewallChangeRequestRow>>({
    request_date: new Date().toISOString().slice(0, 16),
    requester_department: '',
    requester_position: '',
    requester_name: '',
    installed_security_zone: '',
    installed_component: '',
    reason: '',
    before_vendor_model: '',
    before_policy_purpose: '',
    before_policy_detail: '',
    after_vendor_model: '',
    after_policy_purpose: '',
    after_policy_detail: '',
    work_description: '',
    security_review_comment: ''
  })

  const validateToken = useCallback(async () => {
    if (!token) {
      console.log('[FIREWALL DEBUG] No token provided')
      return
    }
    
    try {
      console.log('[FIREWALL DEBUG] Starting token validation for token:', token.substring(0, 8) + '...')
      setValidating(true)
      setLoading(true)
      setError(null)
      
      // add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      )
      
      // use the security definer function to validate token (bypasses rls)
      console.log('[FIREWALL DEBUG] Calling validate_firewall_submission_token RPC')
      const validationPromise = publicRpc('validate_firewall_submission_token', { token_text: token })
      
      const { data: tokenValidation, error: tokenError } = await Promise.race([
        validationPromise,
        timeoutPromise
      ]) as any

      console.log('[FIREWALL DEBUG] Token validation result:', { 
        hasData: !!tokenValidation, 
        dataLength: tokenValidation?.length,
        error: tokenError 
      })

      if (tokenError) {
        console.error('[FIREWALL DEBUG] Token validation error:', tokenError)
        setError(`Invalid or expired submission token: ${tokenError.message || 'Unknown error'}`)
        setTokenValid(false)
        setValidating(false)
        setLoading(false)
        return
      }

      if (!tokenValidation || tokenValidation.length === 0) {
        console.error('[FIREWALL DEBUG] Token validation returned no data')
        setError('Invalid or expired submission token')
        setTokenValid(false)
        setValidating(false)
        setLoading(false)
        return
      }

      // token is valid, now load the firewall change request data
      console.log('[FIREWALL DEBUG] Token is valid, loading firewall change request data')
      const { data: crDataJson, error: crError } = await publicRpc('get_firewall_change_request_by_token', { token_text: token })

      if (crError) {
        console.error('[FIREWALL DEBUG] Error loading firewall change request:', crError)
        setError(`Failed to load firewall change request: ${crError.message || 'Unknown error'}`)
        setTokenValid(false)
        setValidating(false)
        setLoading(false)
        return
      }

      const crData = typeof crDataJson === 'string' ? JSON.parse(crDataJson) : crDataJson
      
      if (!crData) {
        console.error('[FIREWALL DEBUG] No firewall change request data returned')
        setError('Firewall change request not found')
        setTokenValid(false)
        setValidating(false)
        setLoading(false)
        return
      }

      setTokenValid(true)
      console.log('[FIREWALL DEBUG] Token validation successful, loading form data')
      
      // load existing firewall change request data
      setFormData({
        ...crData,
        request_date: crData.request_date ? new Date(crData.request_date).toISOString().slice(0, 16) : null
      })
      console.log('[FIREWALL DEBUG] Form data loaded successfully')
    } catch (err: any) {
      console.error('[FIREWALL DEBUG] Error in token validation:', err)
      setError(err.message || 'Failed to validate submission token')
      setTokenValid(false)
    } finally {
      setValidating(false)
      setLoading(false)
      console.log('[FIREWALL DEBUG] Token validation process completed')
    }
  }, [token, t])

  useEffect(() => {
    if (token && !hasValidated) {
      setHasValidated(true)
      validateToken()
    } else if (!token) {
      setError(t('publicWorkflow.noToken'))
      setValidating(false)
      setLoading(false)
    }
  }, [token, hasValidated, validateToken])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !tokenValid) return

    setSubmitting(true)
    setError(null)

    try {
      console.log('[FIREWALL DEBUG] Starting form submission')
      // use the rpc function to update firewall change request
      const updateData = {
        ...formData,
        request_date: formData.request_date ? new Date(formData.request_date).toISOString() : null
      }

      // remove fields that shouldn't be updated by vendor
      // vendors can only update basic form fields, not status/approval fields or workflow fields
      delete (updateData as any).id
      delete (updateData as any).ship_id
      delete (updateData as any).created_by
      delete (updateData as any).created_at
      delete (updateData as any).submission_token_id
      delete (updateData as any).updated_at  // rpc function sets this automatically
      // workflow fields - only internal users can change these
      delete (updateData as any).status
      delete (updateData as any).approval_status
      delete (updateData as any).approved_by
      delete (updateData as any).approved_at
      delete (updateData as any).rejection_reason
      delete (updateData as any).implemented_by
      delete (updateData as any).implemented_at
      delete (updateData as any).request_no

      console.log('[FIREWALL DEBUG] Calling update_firewall_change_request_by_token RPC with data:', Object.keys(updateData))
      const { data: updatedData, error: updateError } = await publicRpc('update_firewall_change_request_by_token', {
        token_text: token,
        update_data: updateData
      })

      console.log('[FIREWALL DEBUG] Update result:', { hasData: !!updatedData, error: updateError })

      if (updateError) {
        console.error('[FIREWALL DEBUG] Update error:', updateError)
        // if it's a policy error, the token might have been invalidated
        if (updateError.code === '42501') {
          throw new Error('Unable to submit. The submission link may have expired or been deactivated.')
        }
        throw updateError
      }
      
      console.log('[FIREWALL DEBUG] Form submitted successfully')
      setSuccess(true)
    } catch (err: any) {
      console.error('[FIREWALL DEBUG] Error submitting firewall change request:', err)
      console.error('[FIREWALL DEBUG] Error details:', {
        message: err.message,
        code: err.code,
        details: err.details,
        hint: err.hint
      })
      setError(err.message || 'Failed to submit firewall change request')
    } finally {
      setSubmitting(false)
      console.log('[FIREWALL DEBUG] Form submission process completed')
    }
  }

  if (loading || validating) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">{t('publicWorkflow.validating')}</p>
        </div>
      </div>
    )
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('publicWorkflow.invalidTitle')}</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">{error || t('publicWorkflow.invalidDescription')}</p>
            <p className="text-sm text-slate-500 dark:text-slate-500">
              {t('publicWorkflow.contactOrganization')}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('firewall.submittedTitle')}</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t('firewall.submittedDescription')}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-500">
              {t('publicWorkflow.additionalInfo')}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white">
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-6 py-4">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            {t('firewall.submitTitle')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('firewall.submitSubtitle')}
          </p>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        {error && (
          <Card className="mb-6 border-red-200 dark:border-red-800">
            <CardContent className="py-4">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>{t('firewall.information')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.requestDate')}
                    </label>
                    <Input
                      type="datetime-local"
                      value={formData.request_date || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, request_date: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.requesterDepartment')}
                    </label>
                    <Input
                      value={formData.requester_department || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_department: e.target.value }))}
                      placeholder={t('firewall.department')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.requesterPosition')}
                    </label>
                    <Input
                      value={formData.requester_position || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_position: e.target.value }))}
                      placeholder={t('firewall.position')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.requesterName')} *
                    </label>
                    <Input
                      value={formData.requester_name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_name: e.target.value }))}
                      placeholder={t('firewall.name')}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.installedZone')}
                    </label>
                    <Input
                      value={formData.installed_security_zone || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, installed_security_zone: e.target.value }))}
                      placeholder={t('firewall.securityZone')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {t('firewall.installedComponent')}
                    </label>
                    <Input
                      value={formData.installed_component || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, installed_component: e.target.value }))}
                      placeholder={t('fields.component')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('firewall.reason')} *
                  </label>
                  <textarea
                    value={formData.reason || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder={t('firewall.reasonPlaceholder')}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    required
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-4">{t('firewall.beforeChange')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('firewall.vendorModel')}
                      </label>
                      <Input
                        value={formData.before_vendor_model || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, before_vendor_model: e.target.value }))}
                        placeholder={t('firewall.vendorModel')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('firewall.policyPurpose')}
                      </label>
                      <Input
                        value={formData.before_policy_purpose || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, before_policy_purpose: e.target.value }))}
                        placeholder={t('firewall.policyPurpose')}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('firewall.policyDetail')}
                      </label>
                      <textarea
                        value={formData.before_policy_detail || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, before_policy_detail: e.target.value }))}
                        placeholder={t('firewall.policyDetail')}
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-4">{t('firewall.afterChange')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('firewall.vendorModel')}
                      </label>
                      <Input
                        value={formData.after_vendor_model || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, after_vendor_model: e.target.value }))}
                        placeholder={t('firewall.vendorModel')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('firewall.policyPurpose')}
                      </label>
                      <Input
                        value={formData.after_policy_purpose || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, after_policy_purpose: e.target.value }))}
                        placeholder={t('firewall.policyPurpose')}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {t('firewall.policyDetail')}
                      </label>
                      <textarea
                        value={formData.after_policy_detail || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, after_policy_detail: e.target.value }))}
                        placeholder={t('firewall.policyDetail')}
                        className="flex min-h-[80px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('firewall.workDescription')}
                  </label>
                  <textarea
                    value={formData.work_description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, work_description: e.target.value }))}
                    placeholder={t('firewall.workPlaceholder')}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    {t('firewall.securityReview')}
                  </label>
                  <textarea
                    value={formData.security_review_comment || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, security_review_comment: e.target.value }))}
                    placeholder={t('firewall.securityReviewPlaceholder')}
                    className="flex min-h-[100px] w-full rounded-md border border-input bg-white text-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting ? t('publicWorkflow.submitting') : t('firewall.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

